/**
 * The drawer's panel contract — and which panels a caller is ENTITLED to see.
 *
 * ── why this is shared, and what deliberately is not ────────────────────────
 * Christopher, 2026-08-05: the composed experiences — drawer, copilot, command
 * palette, canvases — must be available in the portal, not only the console.
 *
 * The drawer could not simply be moved. Its 738 lines reach into `data/queries`,
 * `crm/experience`, the router's `useNavigate`, and two console features — and
 * the portal has **no router at all**. Copying it would produce two drawers that
 * drift, and the drawer is precisely where record context, timeline and access
 * meet, so drift there is drift in the thing that explains a record.
 *
 * So the SHELL is shared and the PANELS are supplied. Each app registers panels
 * it can actually serve, from its own data layer, and gets one implementation of
 * the rail, the open/close state, the keyboard handling and the empty states.
 *
 * ── entitlement is the part that is not a refactor ──────────────────────────
 * The console's drawer shows access, connections and the audit timeline to
 * whoever has the record open, because everyone who has it open is staff. In a
 * portal that assumption is simply false: "who else can see this" and "the audit
 * trail" are not questions a customer's colleague is automatically entitled to
 * ask, and a panel that answers them by default is a disclosure nobody decided
 * to make.
 *
 * So a panel declares an `entitlement` key and the shell renders only permitted
 * ones. The console permits all of its own (it is staff, and nothing changes);
 * the portal passes the persona's permitted set. Panels are filtered OUT, never
 * rendered-then-emptied — an empty "Access" panel still tells a customer that an
 * access model exists and that they are not allowed to see it.
 */
import type { ReactNode } from 'react'
import type { DrawerScope } from './context'

/** What the drawer is bound to. `entity`/`id` are empty when no record is on
 *  screen — only a `standalone` or non-record-scoped panel is rendered then. */
export interface PanelContext {
  entity: string
  id: string
  scope: DrawerScope
  /**
   * Hand this compact lens off to its canonical routed workspace. The token is
   * deliberately opaque to `@solum/ui`; routing remains a host responsibility.
   * If absent, the panel must not offer a workspace control.
   */
  openWorkspace?: (token: string) => void
  /** Close the current disclosure before handing navigation back to the host. */
  closePanel?: () => void
}

export interface DrawerPanel {
  /** Stable id — also the persisted open-tenant key. */
  key: string
  /**
   * Rail glyph. A `ReactNode` rather than a `string` since #2325: the nav lens
   * needed the shared icon system's History mark, and the alternative was a
   * one-off SVG in a panel registry — the "two of everything" this package
   * exists to prevent, in miniature. A character still works and most panels
   * still pass one; nothing had to change to keep it.
   */
  glyph: ReactNode
  /** Accessible label, and the rail tooltip. */
  label: string
  /**
   * Accessible NAME when the name should say what pressing this does, rather
   * than what the lens is called (#2325 ruling 2 — "Open recent and pinned
   * records" / "Close recent and pinned records").
   *
   * Optional on purpose. A panel that does not pass it keeps `label` as its
   * accessible name, byte-identical to before, so this adds a capability to one
   * lens without restating every other lens's name in a second place. The
   * TOOLTIP stays `label` either way: a tooltip that changed its words on every
   * click is noise, and `aria-expanded` already carries the state for anyone
   * who is listening.
   */
  actionName?: (expanded: boolean) => string
  /** Optional visible name for the compact launcher. */
  compactLabel?: string
  /** A standalone desktop disclosure may explicitly own Escape. */
  closeOnEscape?: boolean
  /**
   * Rendered when a record IS bound. A panel that also wants to render without
   * one (the nav panel, quick create) sets `standalone`.
   */
  render: (ctx: PanelContext) => ReactNode
  /**
   * Shown when a record is EXPECTED and none is bound — the panel is offered
   * (some other lens or a bound record is keeping the drawer open) and this one
   * has nothing to say yet.
   *
   * It is no longer the reason a rail survives an empty application. That
   * argument — "no record on screen, so the tenants explain themselves instead
   * of vanishing" — is overturned by #1466 comment 43562 ruling 3: with nothing
   * bound and nothing standalone, `DrawerShell` renders no rail at all rather
   * than a column of self-explaining placeholders. See its header for the
   * measurement.
   */
  empty?: string
  /**
   * Renders with or without a bound record; `empty` is then unused.
   *
   * Since F-30C this flag also decides whether the DRAWER ITSELF exists with no
   * record on screen: `DrawerShell` shows its rail when a record is bound or
   * when some permitted panel is standalone. So `standalone` is the panel's
   * claim that it answers a question with no record — the console's navigation,
   * work queues, copilot history and capture panels each do; nothing in the
   * portal does. Setting it on a panel that actually needs a record brings back
   * the empty rail the ruling removed.
   */
  standalone?: boolean
  /**
   * Answers at record / mine / team, so the scope bar applies. A panel that only
   * ever describes the record on screen (connections, access, duplicates) leaves
   * this false, and the bar is hidden rather than shown-and-inert — a control
   * that does nothing is worse than no control.
   */
  scoped?: boolean
  /**
   * The permission this panel needs. Omitted = always permitted (a panel that
   * discloses nothing beyond the record already on screen).
   */
  entitlement?: string
  /**
   * This lens's ONE trigger lives in the host's own chrome, not in the drawer.
   *
   * #2328 ruling 74071 §8: *"Recent & Pinned has one surface and one trigger.
   * Move the trigger to the header and remove the drawer-rail duplicate."* The
   * panel itself is not what moves — its surface, its content and its persisted
   * open state stay exactly where #2325 shipped them. What goes is the drawer's
   * own affordance for it: no rail button, no sheet tab, and it is never the
   * compact launcher's first-use default. The host opens it with
   * `requestDrawerPanel(key)` like any other outside caller.
   *
   * It is a flag on the PANEL rather than a list kept in `DrawerShell`, for the
   * reason that file's header already gives about `standalone` and `scoped`: the
   * old dispatch had to exclude two tenants BY NAME, and a list of exceptions
   * maintained beside the list of things is the pair that drifts.
   *
   * A host-triggered panel also cannot keep the drawer alive on its own — see
   * `offers` in `DrawerShell.tsx`. A rail rendered for a lens it does not show
   * is the empty stripe F-30C removed.
   */
  hostTriggered?: boolean
  /**
   * This lens's domain is ALREADY ON THE PAGE, and this finds it (#2406, the
   * leading Organization Detail design: *"nothing in the drawer repeats what
   * the page behind it already says"*).
   *
   * When it returns an element, the lens's mark on the wall rings that card
   * instead of opening a second copy of it in the drawer. When it returns
   * null — the page does not carry the card — the mark opens the lens exactly
   * as before. It is asked at the moment of the press, never cached: the same
   * lens is on the page on a canonical record and absent from a list peek.
   *
   * A FINDER rather than a flag, because only the host knows what its pages
   * render; `@solum/ui` must not name a host's DOM.
   */
  onPage?: () => HTMLElement | null
  /**
   * A count this lens ALREADY governs, drawn riding its mark (#2406 dock).
   *
   * A hook, called with the bound record, returning the count or null. Null
   * and zero both draw nothing — the design's own rule is *"zero shows
   * nothing rather than a zero"*. It is optional because most lenses have no
   * governed count at all, and a mark that invented one (a loaded-row length,
   * a page size) would state a number nobody measured.
   *
   * Must be a stable module-level hook: it is called unconditionally from a
   * component mounted per mark, so React's hook rules hold.
   */
  useCount?: (entity: string, id: string) => DockCount | null
  /*
   * ── A STANDALONE LENS NAMES ITSELF (#2502 §5.2) ───────────────────────────
   * The five members below exist because a lens with no record bound — the
   * portal's Timeline and Help — is a whole surface of its own, not a plate
   * about a record: it needs a sub-line and acts in the drawer head, a foot
   * ("N items · Mark all read"), and a body that is not wrapped in the engraved
   * plate a record lens gets. Each is optional and a panel that passes none of
   * them renders byte-identically to before, so the console's lenses do not
   * move. A standalone lens's `useCount` is also called with no record bound
   * (`entity` and `id` empty): its count is about the caller, not a record.
   */
  /** One quiet line under the lens name in the drawer head. */
  sub?: (ctx: PanelContext) => ReactNode
  /** Acts at the right of the drawer head, before the close. */
  headActions?: (ctx: PanelContext) => ReactNode
  /** The drawer's foot course — a slab joint below the body. */
  footer?: (ctx: PanelContext) => ReactNode
  /**
   * The body is the lens's own surface: no `LensPlate`, no plate head, no
   * inset. A plain lens also names ITSELF in the drawer head even when a record
   * is bound, because it is not about that record.
   */
  plain?: boolean
  /**
   * Which run of marks this lens belongs to on the wall. A cut (the joint) is
   * drawn wherever consecutive marks change group. Absent, the group is derived
   * as before — standalone lenses vs record lenses — so a host that names none
   * gets exactly the old cuts.
   */
  group?: string
  /**
   * The mark's stable ANCHOR key (`data-guide`, #2502 Crew contracts v1) — what
   * a tour step or Help's Show points at, never a selector. `dock.feed` and
   * `dock.help` in the portal.
   */
  guide?: string
}

/** A governed count on a dock mark. */
export interface DockCount {
  /** The number. A floor when `atLeast` is set. */
  n: number
  /** The door capped, or some part was uncounted: `n` is a floor, drawn `n+`. */
  atLeast?: boolean
  /** The whole sentence for assistive technology, e.g. "At least 12 connected". */
  label: string
}

/**
 * The panels this caller may see, in registration order.
 *
 * `permitted === null` means "no entitlement model here" — the console, where
 * every viewer is staff. That is NOT the same as an empty set, and conflating
 * the two is how a portal ends up either showing everything or nothing.
 */
export function entitledPanels(
  panels: readonly DrawerPanel[],
  permitted: readonly string[] | null,
): DrawerPanel[] {
  if (permitted === null) return [...panels]
  const allow = new Set(permitted)
  return panels.filter((p) => p.entitlement === undefined || allow.has(p.entitlement))
}
